import { useState, useEffect, useRef } from "react";
import { useAuth } from "../auth/AuthProvider";
import { useTranslation } from "react-i18next";
import Sidebar from "../components/Sidebar";
import { useCompany } from "../hooks/useCompany";
import { fillForm1096Bytes, mergeCopyABytes } from "../components/Form1099PDF";

const API_URL = process.env.REACT_APP_API_URL;

// ── Formatters ────────────────────────────────────────────────────────────────

const formatEIN = (val) => {
  const d = val.replace(/\D/g, "").slice(0, 9);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}-${d.slice(2)}`;
};

const formatSSN = (val) => {
  const d = val.replace(/\D/g, "").slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};

const formatPhone = (val) => {
  let d = val.replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) d = d.slice(1);
  d = d.slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const formatZip = (val) => val.replace(/\D/g, "").slice(0, 5);

const fmtAmount = (n) =>
  Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Constants ─────────────────────────────────────────────────────────────────

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA",
  "HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
  "DC","PR","GU","VI","AS","MP",
];

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

const BLANK = {
  taxYear: String(currentYear - 1),
  payerLine1: "",
  payerLine2: "",
  payerCity: "",
  payerState: "",
  payerZip: "",
  payerTIN: "",
  ssn: "",
  contactName: "",
  contactPhone: "",
  contactEmail: "",
  faxNumber: "",
  totalForms: "0",
  totalAmount: "",
  totalWithheld: "",
};

/**
 * Converts the 1096 form state into the shape that fillForm1096Bytes expects.
 */
const to1096Data = (f) => ({
  payerLine1:   f.payerLine1,
  payerLine2:   f.payerLine2,
  payerLine3:   [[f.payerCity, f.payerState].filter(Boolean).join(", "), f.payerZip]
                  .filter(Boolean).join("  "),
  payerTIN:     f.payerTIN,
  ssn:          f.ssn,
  contactName:  f.contactName,
  contactPhone: f.contactPhone,
  contactEmail: f.contactEmail,
  faxNumber:    f.faxNumber,
  totalForms:   f.totalForms,
  box1:         f.totalAmount,   // Box 5 — total amount reported
  box4:         f.totalWithheld, // Box 4 — federal tax withheld
});

/**
 * Converts a DB Filing1099 record back into the form data shape that
 * fillPDFBytes / mergeCopyABytes expects.
 */
const filingToFormData = (f) => ({
  taxYear: f.tax_year,
  payerLine1: f.payer_line1,
  payerLine2: f.payer_line2,
  payerLine3: [
    [f.payer_city, f.payer_state].filter(Boolean).join(", "),
    f.payer_zip,
    f.payer_phone,
  ].filter(Boolean).join("  "),
  payerTIN: f.payer_tin,
  recipientName: f.recipient_name,
  recipientAddress: f.recipient_address,
  recipientCityLine: [
    [f.recipient_city, f.recipient_state].filter(Boolean).join(", "),
    f.recipient_zip,
  ].filter(Boolean).join("  "),
  recipientTIN: "",   // not stored
  box1: fmtAmount(f.box1),
  box4: fmtAmount(f.box4),
});

// ── Component ─────────────────────────────────────────────────────────────────

const Generate1096 = () => {
  const auth = useAuth();
  const { t } = useTranslation();
  const { activeCompany, isDemo } = useCompany();

  const [filings1099, setFilings1099] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // "saving" | "saved" | "error"

  const [form, setForm] = useState(BLANK);

  // ── 1096 live preview state ──
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const debounceRef = useRef(null);
  const prevUrlRef = useRef(null);

  // ── Fetch 1099 filings + existing 1096 when year or company changes ──
  useEffect(() => {
    if (!auth?.isAuthenticated || !activeCompany) return;

    const token = auth.user?.access_token;
    const companyId = activeCompany.company_id;
    const headers = { Authorization: `Bearer ${token}` };
    const year = form.taxYear;

    setLoadingData(true);

    Promise.all([
      fetch(`${API_URL}/filings_1099?company_id=${companyId}&tax_year=${year}`, { headers })
        .then((r) => (r.ok ? r.json() : [])).catch(() => []),
      fetch(`${API_URL}/filings_1096?company_id=${companyId}&tax_year=${year}`, { headers })
        .then((r) => (r.ok ? r.json() : null)).catch(() => null),
    ]).then(([f1099s, f1096]) => {
      setFilings1099(f1099s);

      const count = f1099s.length;
      const totalAmt = f1099s.reduce((s, f) => s + (f.box1 || 0), 0);
      const totalWth = f1099s.reduce((s, f) => s + (f.box4 || 0), 0);

      const companyEmail = activeCompany.email || auth.user?.profile?.email || "";
      const companyPhone = formatPhone(activeCompany.phone || "");

      if (f1096) {
        // Pre-populate payer/contact fields from saved 1096.
        // Totals are always recalculated from the current 1099s so they stay in sync.
        setForm({
          taxYear:      year,
          payerLine1:   f1096.payer_line1  || activeCompany.name    || "",
          payerLine2:   f1096.payer_line2  || activeCompany.address || "",
          payerCity:    f1096.payer_city   || activeCompany.city    || "",
          payerState:   f1096.payer_state  || activeCompany.state   || "",
          payerZip:     f1096.payer_zip    || formatZip(activeCompany.zip || ""),
          payerTIN:     f1096.payer_tin    || formatEIN(activeCompany.ein || ""),
          ssn:          f1096.ssn          || "",
          contactName:  f1096.contact_name  || activeCompany.name    || "",
          contactPhone: f1096.contact_phone || companyPhone,
          contactEmail: f1096.contact_email || companyEmail,
          faxNumber:    f1096.fax_number   || "",
          totalForms:   String(count),
          totalAmount:  fmtAmount(totalAmt),
          totalWithheld: fmtAmount(totalWth),
        });
      } else {
        // No saved 1096 — auto-calculate from 1099s and company info
        setForm((prev) => ({
          ...prev,
          taxYear:      year,
          payerLine1:   activeCompany.name    || "",
          payerLine2:   activeCompany.address || "",
          payerCity:    activeCompany.city    || "",
          payerState:   activeCompany.state   || "",
          payerZip:     formatZip(activeCompany.zip || ""),
          payerTIN:     activeCompany.ein ? formatEIN(activeCompany.ein) : prev.payerTIN,
          contactName:  activeCompany.name    || "",
          contactPhone: companyPhone,
          contactEmail: companyEmail,
          totalForms:   String(count),
          totalAmount:  fmtAmount(totalAmt),
          totalWithheld: fmtAmount(totalWth),
        }));
      }

      setLoadingData(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, activeCompany, form.taxYear]);

  // ── Debounced live 1096 preview ──
  useEffect(() => {
    clearTimeout(debounceRef.current);
    setPreviewLoading(true);

    debounceRef.current = setTimeout(async () => {
      try {
        const bytes = await fillForm1096Bytes(to1096Data(form));
        const blob = new Blob([bytes], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
        prevUrlRef.current = url;
        setPreviewUrl(url);
      } catch (err) {
        console.error("1096 preview failed:", err);
      } finally {
        setPreviewLoading(false);
      }
    }, 500);

    return () => clearTimeout(debounceRef.current);
  }, [form]);

  // ── Revoke blob URL on unmount ──
  useEffect(() => {
    return () => { if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current); };
  }, []);

  // ── Setters ──────────────────────────────────────────────────────────────────

  const set    = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const setFmt = (key, fn) => (e) => setForm((prev) => ({ ...prev, [key]: fn(e.target.value) }));

  // ── Download + save ───────────────────────────────────────────────────────────

  const handleDownload = async () => {
    setDownloading(true);
    setSaveStatus("saving");
    try {
      // 1. Download the filled 1096
      const bytes1096 = await fillForm1096Bytes(to1096Data(form));
      const blob1096 = new Blob([bytes1096], { type: "application/pdf" });
      const url1096 = URL.createObjectURL(blob1096);
      const a1096 = document.createElement("a");
      a1096.href = url1096;
      a1096.download = `1096-${form.taxYear}.pdf`;
      a1096.click();
      URL.revokeObjectURL(url1096);

      // 2. Download merged Copy A pages from all stored 1099s
      if (filings1099.length > 0) {
        const formDataList = filings1099.map(filingToFormData);
        const mergedBytes = await mergeCopyABytes(formDataList);
        const blobA = new Blob([mergedBytes], { type: "application/pdf" });
        const urlA = URL.createObjectURL(blobA);
        const aA = document.createElement("a");
        aA.href = urlA;
        aA.download = `1099s-CopyA-${form.taxYear}.pdf`;
        aA.click();
        URL.revokeObjectURL(urlA);
      }

      // 3. Upsert the 1096 to the DB
      const token = auth.user?.access_token;
      const parseAmt = (s) => parseFloat((s || "").replace(/,/g, "")) || 0;
      const res = await fetch(`${API_URL}/filings_1096`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          company_id:     activeCompany.company_id,
          tax_year:       form.taxYear,
          payer_line1:    form.payerLine1,
          payer_line2:    form.payerLine2,
          payer_city:     form.payerCity,
          payer_state:    form.payerState,
          payer_zip:      form.payerZip,
          payer_tin:      form.payerTIN,
          ssn:            form.ssn,
          contact_name:   form.contactName,
          contact_phone:  form.contactPhone,
          contact_email:  form.contactEmail,
          fax_number:     form.faxNumber,
          total_forms:    parseInt(form.totalForms, 10) || 0,
          total_amount:   parseAmt(form.totalAmount),
          total_withheld: parseAmt(form.totalWithheld),
        }),
      });
      setSaveStatus(res.ok ? "saved" : "error");
    } catch {
      setSaveStatus("error");
    } finally {
      setDownloading(false);
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const handleSignOut = () => {
    auth.signOut();
  };

  // ── Styles ───────────────────────────────────────────────────────────────────

  const selectCls = "w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 bg-white";
  const inputCls = isDemo
    ? "w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-gray-100 text-gray-400 cursor-not-allowed"
    : selectCls;
  const labelCls = "block text-xs font-medium text-gray-500 mb-1";

  const StateSelect = ({ value, onChange, disabled }) => (
    <select value={value} onChange={onChange} className={inputCls} disabled={disabled}>
      <option value="">—</option>
      {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
  );

  const Spinner = () => (
    <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
      <svg className="animate-spin h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
      Loading form…
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar handleSignOut={handleSignOut}>
        <div className="flex flex-col flex-1 p-4 gap-4">

          <div>
            <h1 className="text-xl font-semibold text-gray-800">Generate Form 1096</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Annual Summary &amp; Transmittal — one 1096 covers all 1099s for the selected year.
            </p>
          </div>

          <div className="flex gap-4 items-start flex-col xl:flex-row">

            {/* ── LEFT PANEL: FORM ── */}
            <div className="bg-white shadow rounded-lg p-5 w-full xl:w-80 flex-shrink-0 space-y-5">

              {/* 1 · Year */}
              <section>
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">1 · Tax Year</h2>
                <div>
                  <label className={labelCls}>{t("tax_year_label")}</label>
                  <select value={form.taxYear} onChange={set("taxYear")} className={selectCls}>
                    {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>

                {/* 1099 summary */}
                {!loadingData && (
                  <div className="mt-3 bg-gray-50 border border-gray-100 rounded-lg p-3 space-y-1">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Filed 1099s for {form.taxYear}
                    </p>
                    {filings1099.length === 0 ? (
                      <p className="text-xs text-amber-600">
                        No 1099s saved yet — generate at least one on the 1099 page first.
                      </p>
                    ) : (
                      filings1099.map((f) => (
                        <div key={f.filing_id} className="flex justify-between text-xs text-gray-600">
                          <span>{f.recipient_name || f.employee_id}</span>
                          <span className="font-medium">${fmtAmount(f.box1)}</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
                {loadingData && (
                  <p className="text-xs text-gray-400 mt-2">Loading filings…</p>
                )}
              </section>

              {isDemo && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
                  <span className="font-semibold">Demo mode —</span> fields are pre-filled with sample data and cannot be edited. Subscribe to use your own information.
                </div>
              )}

              <hr className="border-gray-100" />

              {/* 2 · Filer Information */}
              <section>
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">2 · Filer Information</h2>
                <div className="space-y-2">
                  <div>
                    <label className={labelCls}>Business Name</label>
                    <input className={inputCls} value={form.payerLine1} onChange={set("payerLine1")} placeholder="Your Business Name" disabled={isDemo} />
                  </div>
                  <div>
                    <label className={labelCls}>Street Address</label>
                    <input className={inputCls} value={form.payerLine2} onChange={set("payerLine2")} placeholder="123 Main St" disabled={isDemo} />
                  </div>
                  <div>
                    <label className={labelCls}>City</label>
                    <input className={inputCls} value={form.payerCity} onChange={set("payerCity")} placeholder="Miami" disabled={isDemo} />
                  </div>
                  <div className="flex gap-2">
                    <div className="w-20 flex-shrink-0">
                      <label className={labelCls}>State</label>
                      <StateSelect value={form.payerState} onChange={set("payerState")} disabled={isDemo} />
                    </div>
                    <div className="flex-1">
                      <label className={labelCls}>ZIP</label>
                      <input
                        className={inputCls}
                        value={form.payerZip}
                        onChange={setFmt("payerZip", formatZip)}
                        placeholder="33101"
                        inputMode="numeric"
                        maxLength={5}
                        disabled={isDemo}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Filer EIN</label>
                    <input
                      className={inputCls}
                      value={form.payerTIN}
                      onChange={setFmt("payerTIN", formatEIN)}
                      placeholder="12-3456789"
                      inputMode="numeric"
                      disabled={isDemo}
                    />
                  </div>
                </div>
              </section>

              <hr className="border-gray-100" />

              {/* 3 · Contact Information */}
              <section>
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">3 · Contact Information</h2>
                <div className="space-y-2">
                  <div>
                    <label className={labelCls}>Name of Person to Contact</label>
                    <input className={inputCls} value={form.contactName} onChange={set("contactName")} placeholder="Jane Smith" disabled={isDemo} />
                  </div>
                  <div>
                    <label className={labelCls}>Phone</label>
                    <div className={`flex items-center border border-gray-200 rounded-lg overflow-hidden bg-white ${isDemo ? "opacity-60" : "focus-within:ring-2 focus-within:ring-green-400"}`}>
                      <span className="px-2.5 text-sm text-gray-400 border-r border-gray-200 bg-gray-50 select-none">+1</span>
                      <input
                        className={`flex-1 px-3 py-1.5 text-sm focus:outline-none ${isDemo ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white"}`}
                        value={form.contactPhone}
                        onChange={setFmt("contactPhone", formatPhone)}
                        placeholder="(305) 555-0000"
                        inputMode="tel"
                        disabled={isDemo}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Email Address</label>
                    <input className={inputCls} value={form.contactEmail} onChange={set("contactEmail")} placeholder="contact@business.com" type="email" disabled={isDemo} />
                  </div>
                  <div>
                    <label className={labelCls}>Fax Number (optional)</label>
                    <div className={`flex items-center border border-gray-200 rounded-lg overflow-hidden bg-white ${isDemo ? "opacity-60" : "focus-within:ring-2 focus-within:ring-green-400"}`}>
                      <span className="px-2.5 text-sm text-gray-400 border-r border-gray-200 bg-gray-50 select-none">+1</span>
                      <input
                        className={`flex-1 px-3 py-1.5 text-sm focus:outline-none ${isDemo ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white"}`}
                        value={form.faxNumber}
                        onChange={setFmt("faxNumber", formatPhone)}
                        placeholder="(305) 555-0001"
                        inputMode="tel"
                        disabled={isDemo}
                      />
                    </div>
                  </div>
                </div>
              </section>

              <hr className="border-gray-100" />

              {/* 4 · Summary Totals */}
              <section>
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">4 · Summary Totals</h2>
                <p className="text-xs text-gray-400 mb-3">
                  Auto-calculated from saved 1099s. Edit if needed.
                </p>
                <div className="space-y-2">
                  <div>
                    <label className={labelCls}>Box 2 — SSN (individual filers only)</label>
                    <input
                      className={inputCls}
                      value={form.ssn}
                      onChange={setFmt("ssn", formatSSN)}
                      placeholder="123-45-6789"
                      inputMode="numeric"
                      disabled={isDemo}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Box 3 — Number of Forms</label>
                    <input
                      className={inputCls}
                      value={form.totalForms}
                      onChange={set("totalForms")}
                      placeholder="0"
                      inputMode="numeric"
                      disabled={isDemo}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Box 4 — Federal Tax Withheld</label>
                    <input
                      className={inputCls}
                      value={form.totalWithheld}
                      onChange={set("totalWithheld")}
                      placeholder="0.00"
                      disabled={isDemo}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Box 5 — Total Amount Reported</label>
                    <input
                      className={inputCls}
                      value={form.totalAmount}
                      onChange={set("totalAmount")}
                      placeholder="0.00"
                      disabled={isDemo}
                    />
                  </div>
                </div>
              </section>

              <hr className="border-gray-100" />

              <button
                onClick={handleDownload}
                disabled={downloading || filings1099.length === 0}
                className="w-full py-2.5 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white text-sm font-semibold rounded-lg transition"
              >
                {downloading
                  ? "Generating…"
                  : `↓ Download 1096 + Copy A (${filings1099.length} 1099${filings1099.length !== 1 ? "s" : ""})`}
              </button>

              {filings1099.length === 0 && !loadingData && (
                <p className="text-xs text-amber-600 text-center -mt-2">
                  Save at least one 1099 before generating the 1096.
                </p>
              )}

              {saveStatus === "saved" && (
                <p className="text-xs text-green-600 text-center">1096 saved to records.</p>
              )}
              {saveStatus === "error" && (
                <p className="text-xs text-red-500 text-center">Could not save 1096 — check connection.</p>
              )}

            </div>

            {/* ── RIGHT PANEL: LIVE 1096 PREVIEW ── */}
            <div className="flex-1 min-w-0">
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 bg-gray-50">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Live Preview — Form 1096
                  </span>
                  <span className={`text-xs ${previewLoading ? "text-amber-500" : "text-gray-400"}`}>
                    {previewLoading ? "Updating…" : "Up to date"}
                  </span>
                </div>
                {previewUrl ? (
                  <iframe
                    key={previewUrl}
                    src={`${previewUrl}#page=2`}
                    title="1096 Preview"
                    className="w-full"
                    style={{ height: "700px", border: "none", display: "block" }}
                  />
                ) : (
                  <Spinner />
                )}
              </div>
            </div>

          </div>
        </div>
      </Sidebar>
    </div>
  );
};

export default Generate1096;
